'use client'

import React, { Suspense, useLayoutEffect, useState } from 'react'
import { Button, Flex } from '@mantine/core'
import { usePathname, useRouter } from 'next/navigation'
import { MessagesTextCircleIcon } from '@/components/Icons/MessagesTextCircleIcon'
import { useContactSupport } from '@/hooks/useContactSupport'
import NavBarHeader from '@/components/NavBar/NavBarHeader'
import NavBarSubheader from '@/components/NavBar/NavBarSubheader'
import StoreSearchDrawer from '@/components/Store/StoreSearchDrawer'
import { useIsMobile } from '@/lib/mobileDetector'
import { isAnyStorePage, isStoreHomePage } from '@/lib/namedRoutes'
import { useUserContext } from '@/contexts/UserContext'
import { StoreAnalytics, trackStoreEvent } from '@nile/mantine-theme/analytics'
import { AnimatePresence, motion } from 'framer-motion'
import { useGeneralAiStatus } from '@/hooks/useGeneralAiStatus'
import { tokens } from '@nile/mantine-theme/theme/styles/variables'
import { isFocusedFlowPage } from '@/lib/focusedFlow'
import { isGenAiFlowPage } from '@/lib/utilities'
import TopBar from '@/components/Refresh/MarketingBar/TopBar'
import { categoryHasCustomHeader } from '@/lib/hardcoded-data/CategoryPages/getCategoryPageData'

interface NavBarProps {
    logoOnly?: boolean
}

const NavBar: React.FC<React.PropsWithChildren<NavBarProps>> = ({ children, logoOnly = false }) => {
    const isMobile = useIsMobile()
    const [showSearchDrawer, setShowSearchDrawer] = useState<boolean>(false)
    const [hoverButtonShrink] = useState<boolean>(false)
    const pathname = usePathname()
    const isSearchPath = pathname.startsWith('/store/search')
    const router = useRouter()
    const { user } = useUserContext()
    const { contactUsAction } = useContactSupport({
        defaultLabel: 'Text us',
        defaultAction: (openSms) => (user ? openSms() : router.push('/get-started')),
        onAction: (destination) =>
            trackStoreEvent(StoreAnalytics.CLICKED_FLOATING_TEXT_US, {
                destination: destination === 'message-center' ? 'message-center' : user ? 'sms' : 'get-started',
            }),
    })
    const { isFromGenAi } = useGeneralAiStatus()
    const isGenAiFlow = isGenAiFlowPage(pathname)

    const shouldShowPrimaryColors = isGenAiFlow && isFromGenAi
    const storeCategorySlug = pathname.match(/^\/store\/([^/]+)/)?.[1]
    const isStoreCategoryPage =
        !!storeCategorySlug &&
        pathname.startsWith('/store/') &&
        !pathname.startsWith('/store/search') &&
        !isStoreHomePage(pathname)

    const hasCategoryHeader = isStoreCategoryPage && !!storeCategorySlug && categoryHasCustomHeader(storeCategorySlug)
    const shouldShowSubheader =
        (isAnyStorePage(pathname) || pathname.startsWith('/store/search')) && !hasCategoryHeader

    const showTopBar = isAnyStorePage(pathname)
    const [isTopBarVisible, setIsTopBarVisible] = useState(true)

    useLayoutEffect(() => {
        if (!showTopBar) return
        setIsTopBarVisible(true)

        let isShowing = true
        const handleScroll = () => {
            const y = window.scrollY
            if (y === 0 && !isShowing) {
                isShowing = true
                setIsTopBarVisible(true)
            } else if (y > 0 && isShowing) {
                isShowing = false
                setIsTopBarVisible(false)
            }
        }
        handleScroll()
        window.addEventListener('scroll', handleScroll, { passive: true })
        return () => window.removeEventListener('scroll', handleScroll)
    }, [showTopBar, pathname])

    if (
        pathname === '/ai/chat' ||
        pathname === '/ai/onboarding' ||
        pathname === '/ai/health-records' ||
        pathname === '/clinician-hiring' ||
        pathname === '/work-with-us' ||
        isFocusedFlowPage(pathname)
    ) {
        return <>{children}</>
    }

    const getNavBackground = () => {
        if (isGenAiFlow && isFromGenAi) {
            return tokens.gradients.genaiOnboarding
        }
        return '#F5F1ED'
    }

    return (
        <Flex direction="column" h="100%">
            {!logoOnly && showTopBar && (
                <AnimatePresence initial={false}>
                    {isTopBarVisible && (
                        <motion.div
                            role="region"
                            aria-label="Announcement"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25, ease: 'easeInOut' }}
                            style={{ overflow: 'hidden' }}
                        >
                            <TopBar />
                        </motion.div>
                    )}
                </AnimatePresence>
            )}

            <Flex
                component="header"
                pos={'sticky'}
                direction="column"
                top={0}
                style={{
                    zIndex: 3,
                    overflowX: 'hidden',
                }}
            >
                <Flex
                    direction="column"
                    bg={'#F5F1ED'}
                    style={{
                        overflowX: 'hidden',
                        background: getNavBackground(),
                    }}
                >
                    <Flex bg={'#F5F1ED'}>
                        <NavBarHeader
                            setShowSearchDrawer={setShowSearchDrawer}
                            shouldShowPrimaryColors={shouldShowPrimaryColors}
                            isFromGenAi={isGenAiFlow && isFromGenAi}
                            logoOnly={logoOnly}
                        />
                    </Flex>
                </Flex>
            </Flex>

            {!logoOnly && (
                <motion.div
                    // Stays mounted when the subheader renders nothing so its user fetch and route guards still run.
                    role={shouldShowSubheader ? 'region' : undefined}
                    aria-label={shouldShowSubheader ? 'Search and browse' : undefined}
                    style={{
                        background: getNavBackground(),
                        overflow: 'hidden',
                        paddingLeft: 12,
                        paddingRight: 12,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Suspense>
                        <NavBarSubheader
                            shouldShowSubheader={shouldShowSubheader}
                            setShowSearchDrawer={setShowSearchDrawer}
                            shouldShowPrimaryColors={shouldShowPrimaryColors}
                        />
                    </Suspense>
                </motion.div>
            )}

            <Flex direction="column" flex={1} w={'100%'}>
                {children}
            </Flex>

            {!logoOnly && (
                <>
                    <StoreSearchDrawer showSearchDrawer={showSearchDrawer} setShowSearchDrawer={setShowSearchDrawer} />
                    {isAnyStorePage(pathname) && !isSearchPath && renderSupportHoverButton()}
                </>
            )}
        </Flex>
    )

    function renderSupportHoverButton() {
        const hideCareTeam = true
        if (hideCareTeam) return null

        return (
            <Button
                onClick={contactUsAction}
                pos="fixed"
                bottom={20}
                right={hoverButtonShrink ? 20 : '50%'}
                h="var(--spacing-52, 52px)"
                p={0}
                w={hoverButtonShrink ? 'var(--spacing-52, 52px)' : 345}
                style={{
                    transform: hoverButtonShrink ? '' : 'translateX(50%)',
                    transition:
                        'width 0.75s cubic-bezier(0.4, 0, 0.2, 1), left 0.75s cubic-bezier(0.4, 0, 0.2, 1), right 0.75s cubic-bezier(0.4, 0, 0.2, 1), transform 0.75s cubic-bezier(0.4, 0, 0.2, 1)',
                    zIndex: 1000,
                }}
            >
                <Flex justify="center" align={'center'} gap={8}>
                    <MessagesTextCircleIcon color={'white'} />
                    {!hoverButtonShrink && 'Chat with our care team'}
                </Flex>
            </Button>
        )
    }
}

export default NavBar
